import { QueryClient } from '@tanstack/react-query';
import { getRetryAfter } from '@/libs/error/error.utils';
import { isAppError } from '../error/error';
import { HttpStatusCode } from '../http/http.types';
import type { QueryClientConfig, RetryConfig } from './query-client.types';

const queryClientRegistry: QueryClient[] = [];

/**
 * The retry decision functions derived from a `RetryConfig`.
 */
export type RetryPolicy = {
  /** Whether another attempt is allowed for a failure. */
  shouldRetry: (failureCount: number, error: unknown) => boolean;
  /** Backoff before the next attempt, in milliseconds. */
  retryDelay: (attemptIndex: number, error: unknown) => number;
};

/**
 * Builds the retry decision functions for a retry configuration.
 *
 * Exported so a service can scope a per-query override (a different limit for one
 * status category, derived from the same config) without reimplementing the policy.
 * TanStack replaces the client-level retry option wholesale when a query sets its own,
 * so an override has to carry the whole policy.
 *
 * @param retry - Retry configuration for the client or the scoped query
 * @returns The retry and delay decision functions
 */
export function createRetryPolicy(retry: RetryConfig): RetryPolicy {
  /**
   * Determines if an error should be retried based on configuration.
   */
  function shouldRetry(failureCount: number, error: unknown): boolean {
    if (!isAppError(error)) {
      return failureCount < retry.limits.default;
    }

    const statusCode = error.context?.statusCode as number | undefined;
    const errorCode = error.code;

    // Permanent failures - don't retry
    if (errorCode && retry.nonRetryable.includes(errorCode)) {
      return false;
    }

    // Status code based retry logic
    if (statusCode === HttpStatusCode.NOT_FOUND) {
      return failureCount < (retry.limits.notFound ?? retry.limits.default);
    }

    if (statusCode === HttpStatusCode.TOO_MANY_REQUESTS) {
      return failureCount < (retry.limits.rateLimited ?? retry.limits.serverError ?? retry.limits.default);
    }

    if (statusCode !== undefined && statusCode >= HttpStatusCode.INTERNAL_SERVER_ERROR) {
      return failureCount < (retry.limits.serverError ?? retry.limits.default);
    }

    // Other 4xx: Client errors - don't retry
    if (statusCode !== undefined && statusCode >= HttpStatusCode.BAD_REQUEST) {
      return false;
    }

    return failureCount < retry.limits.default;
  }

  /**
   * Calculates retry delay with exponential backoff.
   * Formula: min(initial * 2^attemptIndex, max)
   */
  function retryDelay(attemptIndex: number, error: unknown): number {
    const delays = retry.delays;

    if (isAppError(error)) {
      const statusCode = error.context?.statusCode as number | undefined;

      // 429: the server is telling us to slow down. Back off hard (start at 2s,
      // grow exponentially) and only retry once — retrying fast into the same
      // burst amplifies the spike (Sentry PUBKY-APP-B3). Honor the server's
      // Retry-After hint when present (the HTTP error factory parses it into
      // context.retryAfter, in seconds), clamped between the 2s floor and the
      // configured max; the configured backoff is the fallback.
      if (statusCode === HttpStatusCode.TOO_MANY_REQUESTS) {
        const retryAfterSeconds = getRetryAfter(error);
        if (retryAfterSeconds !== undefined) {
          return Math.min(Math.max(retryAfterSeconds * 1_000, 2_000), delays.default.max);
        }
        return Math.max(2_000, Math.min(delays.default.initial * 2 ** attemptIndex, delays.default.max));
      }

      // 404: Use notFound delays if configured
      if (statusCode === HttpStatusCode.NOT_FOUND && delays.notFound) {
        return Math.min(delays.notFound.initial * 2 ** attemptIndex, delays.notFound.max);
      }

      // 5xx: Use serverError delays if configured
      if (statusCode !== undefined && statusCode >= HttpStatusCode.INTERNAL_SERVER_ERROR && delays.serverError) {
        return Math.min(delays.serverError.initial * 2 ** attemptIndex, delays.serverError.max);
      }
    }

    // Default delays
    return Math.min(delays.default.initial * 2 ** attemptIndex, delays.default.max);
  }

  return { shouldRetry, retryDelay };
}

/**
 * Cancels and clears all query clients created via createQueryClient.
 */
export function clearAllQueryClients(): void {
  for (const client of queryClientRegistry) {
    client.cancelQueries();
    client.clear();
  }
}

/**
 * Creates a TanStack QueryClient with configurable retry behavior.
 *
 * Each service can define its own retry strategy:
 * - Which error types are non-retryable (permanent failures)
 * - Retry limits per status code category
 * - Exponential backoff delays
 *
 * @param config - Configuration for retry behavior, stale time, and cache time
 * @returns A configured QueryClient instance
 */
export function createQueryClient(config: QueryClientConfig): QueryClient {
  const { retry, staleTime = 0, gcTime = 30 * 60 * 1000 } = config;
  const { shouldRetry, retryDelay } = createRetryPolicy(retry);

  const client = new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay: retryDelay,
        staleTime,
        gcTime,
      },
      mutations: {
        // Mutations are user actions: never blind-retry them (explicit over
        // TanStack's default of 0 so the intent is visible and enforced).
        retry: false,
      },
    },
  });

  queryClientRegistry.push(client);

  return client;
}
